/**
 * 15. API / Webhook Admin — API keys, webhook subscriptions, delivery
 * logs, dead-letter queue.
 *
 * Arabic and English (C7): every word from the catalogs; the tabs are chosen
 * by id, never by their words. Scopes, events and topics are codes and are
 * shown as sent, left to right.
 *
 * UI-HONEST: "New API client" opens NewApiClient, wired to
 * `POST /v1/api-clients`. The dead-letter tab lists dead-lettered messages
 * only (`?status=DeadLetter`): it used to list every outbox message, a sent
 * `tenant.provisioned` included, beside Replay and Discard buttons with no
 * handler. Replaying an event is staff work on the platform (no plan gives a
 * customer a replay, and the retry route would re-run whatever the message
 * started), and no route discards one, so both buttons were removed and the
 * tab is read only. The "Rate limits" card was removed too: it showed two
 * figures typed into this file (60,000 a minute; 10 sign-ins a minute) that
 * no setting holds and that did not match the limits the API enforces.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  Button,
  DataTable,
  Tabs,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { formatDateTime, formatQuantity } from "@/lib/money";
import { NewApiClient } from "./NewApiClient";

interface ApiClient {
  id: string;
  name: string;
  clientId: string;
  scopes: string[];
  status: "Active" | "Revoked";
  lastUsedAt: string | null;
}

interface WebhookSubscription {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  lastDeliveryAt: string | null;
  failures: number;
}

interface DeadLetterMessage {
  id: string;
  topic: string;
  eventId: string;
  status: "Pending" | "Sending" | "Sent" | "DeadLetter";
  attemptCount: number;
  nextAttemptAt: string | null;
}

type Tab = "clients" | "webhooks" | "deadLetter";
const TABS: Tab[] = ["clients", "webhooks", "deadLetter"];

export function ApiWebhooksPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>("clients");
  const [creating, setCreating] = useState(false);
  const { data: clients, isLoading: clientsLoading } = useApiList<ApiClient>(
    ["admin", "api-clients"],
    "/admin/api-clients",
  );
  const { data: webhooks, isLoading: webhooksLoading } =
    useApiList<WebhookSubscription>(
      ["integrations", "webhooks"],
      "/integrations/webhooks",
    );
  const { data: deadLetters, isLoading: dlqLoading } =
    useApiList<DeadLetterMessage>(
      ["integrations", "dead-letter"],
      "/integrations/dead-letter?status=DeadLetter",
    );

  const codes = (values: string[] | undefined) =>
    values && values.length > 0 ? (
      <span className="atlas-mono" dir="ltr">
        {values.join(", ")}
      </span>
    ) : (
      "—"
    );

  return (
    <div>
      <PageHeader
        title={t("admin.apiWebhooks.title")}
        subtitle={t("admin.apiWebhooks.subtitle")}
        actions={
          <Button
            variant="primary"
            aria-expanded={creating}
            onClick={() => {
              // Opens the form, or closes it when it is open (aria-expanded).
              setCreating((open) => !open);
              setTab("clients");
            }}
          >
            {t("admin.apiWebhooks.newClient")}
          </Button>
        }
      />
      {creating && <NewApiClient onClose={() => setCreating(false)} />}
      <Tabs
        tabs={TABS.map((id) => ({
          id,
          label: t(`admin.apiWebhooks.tabs.${id}`),
        }))}
        active={tab}
        onChange={setTab}
      />

      {tab === "clients" && (
        <Card title={t("admin.apiWebhooks.tabs.clients")}>
          {clientsLoading || !clients ? (
            <Skeleton rows={5} />
          ) : (
            <DataTable<ApiClient>
              rows={clients.items}
              columns={[
                { key: "name", label: t("fields.name") },
                {
                  key: "clientId",
                  label: t("admin.apiWebhooks.clientId"),
                  render: (r) => (
                    <span className="atlas-mono">{r.clientId}</span>
                  ),
                },
                {
                  key: "scopes",
                  label: t("admin.apiWebhooks.scopes"),
                  render: (r) => codes(r.scopes),
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (r) => (
                    <StatusBadge status={r.status} type="ApiClient" />
                  ),
                },
                {
                  key: "lastUsedAt",
                  label: t("admin.apiWebhooks.lastUsed"),
                  render: (r) => formatDateTime(r.lastUsedAt),
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "webhooks" && (
        <Card title={t("admin.apiWebhooks.webhooksTitle")}>
          {webhooksLoading || !webhooks ? (
            <Skeleton rows={5} />
          ) : (
            <DataTable<WebhookSubscription>
              rows={webhooks.items}
              columns={[
                {
                  key: "url",
                  label: t("admin.apiWebhooks.endpoint"),
                  render: (r) => (
                    <span className="atlas-mono" dir="ltr">
                      {r.url}
                    </span>
                  ),
                },
                {
                  key: "events",
                  label: t("admin.apiWebhooks.events"),
                  render: (r) => codes(r.events),
                },
                {
                  key: "active",
                  label: t("fields.status"),
                  render: (r) =>
                    r.active ? (
                      <Badge tone="success">{t("fields.active")}</Badge>
                    ) : (
                      <Badge tone="default">
                        {t("admin.apiWebhooks.paused")}
                      </Badge>
                    ),
                },
                {
                  key: "lastDeliveryAt",
                  label: t("admin.apiWebhooks.lastDelivery"),
                  render: (r) => formatDateTime(r.lastDeliveryAt),
                },
                {
                  key: "failures",
                  label: t("admin.apiWebhooks.failures"),
                  numeric: true,
                  render: (r) => formatQuantity(r.failures, 0),
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "deadLetter" && (
        <Card title={t("admin.apiWebhooks.deadLetterTitle")}>
          {dlqLoading || !deadLetters ? (
            <Skeleton rows={5} />
          ) : (
            <DataTable<DeadLetterMessage>
              rows={deadLetters.items}
              columns={[
                {
                  key: "topic",
                  label: t("admin.apiWebhooks.topic"),
                  render: (r) => (
                    <span className="atlas-mono" dir="ltr">
                      {r.topic}
                    </span>
                  ),
                },
                {
                  key: "eventId",
                  label: t("admin.apiWebhooks.eventId"),
                  render: (r) => (
                    <span className="atlas-mono">{r.eventId.slice(0, 8)}</span>
                  ),
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (r) => (
                    <StatusBadge status={r.status} type="OutboxMessage" />
                  ),
                },
                {
                  key: "attemptCount",
                  label: t("admin.apiWebhooks.attempts"),
                  numeric: true,
                  render: (r) => formatQuantity(r.attemptCount, 0),
                },
                {
                  key: "nextAttemptAt",
                  label: t("admin.apiWebhooks.nextAttempt"),
                  render: (r) => formatDateTime(r.nextAttemptAt),
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
